import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Typography,
    Chip,
    TextField,
    InputAdornment,
} from "@material-ui/core";
import {
    Add as AddIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Cancel as CancelIcon,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    searchInput: {
        padding: theme.spacing(1),
        display: "flex",
        alignItems: "center",
    },
}));

const EmailCampaigns = () => {
    const classes = useStyles();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [searchParam, setSearchParam] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [deletingCampaign, setDeletingCampaign] = useState(null);
    const [cancelingCampaign, setCancelingCampaign] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);

    useEffect(() => {
        fetchCampaigns();
    }, [searchParam, pageNumber]);

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/email-campaigns", {
                params: { searchParam, page: pageNumber },
            });
            setCampaigns(data.campaigns);
            setHasMore(data.hasMore);
        } catch (err) {
            toast.error("Erro ao carregar campanhas");
        }
        setLoading(false);
    };

    const handleDeleteCampaign = async (campaignId) => {
        try {
            await api.delete(`/email-campaigns/${campaignId}`);
            toast.success("Campanha removida com sucesso");
            fetchCampaigns();
        } catch (err) {
            toast.error("Erro ao remover campanha");
        }
        setDeletingCampaign(null);
        setConfirmModalOpen(false);
    };

    const handleCancelCampaign = async (campaignId) => {
        try {
            await api.post(`/email-campaigns/${campaignId}/cancel`);
            toast.success("Campanha cancelada com sucesso");
            fetchCampaigns();
        } catch (err) {
            toast.error("Erro ao cancelar campanha");
        }
        setCancelingCampaign(null);
        setCancelModalOpen(false);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "draft": return "default";
            case "scheduled": return "primary";
            case "sending": return "secondary";
            case "paused": return "secondary";
            case "completed": return "default";
            case "failed": return "error";
            default: return "default";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "draft": return "Rascunho";
            case "scheduled": return "Agendada";
            case "sending": return "Enviando";
            case "paused": return "Pausada";
            case "completed": return "Concluída";
            case "failed": return "Falha";
            default: return status;
        }
    };

    const handleEditCampaign = (campaignId) => {
        history.push(`/email-campaigns/${campaignId}`);
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title="Excluir Campanha"
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => handleDeleteCampaign(deletingCampaign?.id)}
            >
                Você tem certeza que deseja excluir a campanha "{deletingCampaign?.name}"?
            </ConfirmationModal>

            <ConfirmationModal
                title="Cancelar Campanha"
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                onConfirm={() => handleCancelCampaign(cancelingCampaign?.id)}
            >
                Você tem certeza que deseja cancelar o agendamento da campanha "{cancelingCampaign?.name}"? Ela voltará para o status de rascunho.
            </ConfirmationModal>

            <MainHeader>
                <Title>Campanhas de E-mail</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder="Pesquisar..."
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => history.push("/email-campaigns/new")}
                    >
                        <AddIcon /> Nova Campanha
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>

            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Contatos</TableCell>
                                <TableCell align="center">Enviados</TableCell>
                                <TableCell align="center">Abertura</TableCell>
                                <TableCell align="center">Agendamento</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRowSkeleton columns={7} />
                            ) : (
                                <>
                                    {campaigns.map((campaign) => (
                                        <TableRow
                                            key={campaign.id}
                                            onClick={() => history.push(`/email-campaigns/${campaign.id}/details`)}
                                            style={{ cursor: "pointer" }}
                                            hover
                                        >
                                            <TableCell>{campaign.name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusText(campaign.status)}
                                                    color={getStatusColor(campaign.status)}
                                                    size="small"
                                                    style={campaign.status === "completed" ? { backgroundColor: "#4caf50", color: "#fff" } :
                                                        campaign.status === "paused" ? { backgroundColor: "#ff9800", color: "#fff" } : {}}
                                                />
                                            </TableCell>
                                            <TableCell align="center">{campaign.totalContacts}</TableCell>
                                            <TableCell align="center">
                                                {campaign.sentCount} / {campaign.totalContacts}
                                                {campaign.totalContacts > 0 && (
                                                    <Typography variant="caption" display="block">
                                                        {Math.round((campaign.sentCount / campaign.totalContacts) * 100)}%
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {campaign.openedCount}
                                                {campaign.sentCount > 0 && (
                                                    <Typography variant="caption" display="block">
                                                        {Math.round((campaign.openedCount / campaign.sentCount) * 100)}%
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {campaign.scheduledAt ? (
                                                    <>
                                                        {format(parseISO(campaign.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                                                        {campaign.sendWindowStart && (
                                                            <Typography variant="caption" display="block">
                                                                Janela: {campaign.sendWindowStart.substring(0, 5)} - {campaign.sendWindowEnd.substring(0, 5)}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : "-"}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditCampaign(campaign.id)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                {(campaign.status === "scheduled" || campaign.status === "sending" || campaign.status === "paused") && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setCancelingCampaign(campaign);
                                                            setCancelModalOpen(true);
                                                        }}
                                                        title="Cancelar agendamento"
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                )}
                                                {(campaign.status === "draft" || campaign.status === "failed") && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setDeletingCampaign(campaign);
                                                            setConfirmModalOpen(true);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {campaigns.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                Nenhuma campanha encontrada
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default EmailCampaigns;
